// A glass switch drawn as the REAL PLATE (2026-10-03) — the desktop's card for
// every product in core/devices/glassPlates: round touch buttons in the wall
// order, the "||" master, fan columns (+ / fan / −) with their LED speed strip,
// the 3-pin socket, and the plate itself in its real finish, MATT or GLOSSY
// (core/devices/glassFinish — set from the card's ⋮ menu).
//
// Behaviour is the switch card's, unchanged:
//   • state comes ONLY from the device (core/hooks/useRelayStatus) — a press
//     publishes and changes nothing until the device echoes; GREY = not heard yet
//   • master = allon / alloff (the Tuya DP differs per product and the ESP8266
//     fleet cannot take one — see core/devices/deviceCatalog)
//   • a panel fan is `<channel>-<speed>` on the panel ladder (statusFormat);
//     the standalone 2M fan is fanCommand() on its own ladder (core/devices/fan)
//   • right-click a switch: Schedule / Button Settings, as on every card
//
// Everything is sized from ONE unit `b` (a button's diameter), computed from the
// plate's measured width, so a 2M and an 8M plate keep the photo's proportions
// at any card width.

import React, {
  useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState,
} from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Card, Box, Typography, ButtonBase, Menu, MenuItem, ListItemIcon, ListItemText, Tooltip,
} from '@mui/material';
import ScheduleIcon from '@mui/icons-material/Schedule';
import TuneIcon from '@mui/icons-material/Tune';
import { useApp } from '../../../core/state/AppContext';
import { useRelayStatus } from '../../../core/hooks/useRelayStatus';
import { useDeviceStatus } from '../../../core/mqtt/useDeviceStatus';
import { getOutputs } from '../../../core/api/api';
import { rowForOutput } from '../../../core/devices/buttonStatus';
import { plateFor, switchColumns } from '../../../core/devices/glassPlates';
import {
  FINISH, cachedFinish, getGlassFinish, onFinishChange,
} from '../../../core/devices/glassFinish';
import { FAN_OFF, FAN_SPEEDS as PANEL_SPEEDS } from '../../../core/devices/statusFormat';
import {
  FAN_SPEEDS as SOLO_SPEEDS, fanCommand, parseFanSpeed,
} from '../../../core/devices/fan';
import { publishCommand } from '../../../core/utils/deviceController';
import { fetchSeedStatus } from '../../../core/devices/deviceStatus';
import { CARD_TYPE } from '../../../core/devices/buttonLayout';
import DeviceStatusDot from './DeviceStatusDot';
import DeviceCardMenu from './DeviceCardMenu';
import ButtonSettingsDialog from '../../shared/ButtonSettingsDialog';

// ── The two finishes ─────────────────────────────────────────────────────────
// MATT: flat charcoal with a fine satin grain, soft diffuse shadow, satin
// buttons, no reflections — the photos. GLOSSY: deep black glass, a diagonal
// sheen across the plate, a bright edge, mirror-chrome buttons.
const LOOK = {
  [FINISH.MATT]: {
    plate: {
      background: 'linear-gradient(160deg, #4b4e53 0%, #3d4045 55%, #35373b 100%)',
      boxShadow: '0 12px 26px rgba(0,0,0,0.38), inset 0 1px 0 rgba(255,255,255,0.07), inset 0 0 0 1px rgba(255,255,255,0.05)',
    },
    // Satin grain: a very fine dot pattern, barely there.
    grain: 'radial-gradient(rgba(255,255,255,0.035) 0.6px, transparent 0.7px)',
    sheen: null,
    button: 'radial-gradient(circle at 38% 32%, #5d6066 0%, #3a3d42 55%, #2b2d31 100%)',
    buttonEdge: 'inset 0 1px 1px rgba(255,255,255,0.10), inset 0 -2px 3px rgba(0,0,0,0.45), 0 1px 2px rgba(0,0,0,0.35)',
    label: 'rgba(255,255,255,0.72)',
    socket: 'linear-gradient(160deg, #3a3d41, #2c2e32)',
  },
  [FINISH.GLOSSY]: {
    plate: {
      background: 'linear-gradient(160deg, #1d1f23 0%, #0e0f12 58%, #050506 100%)',
      boxShadow: '0 16px 32px rgba(0,0,0,0.55), inset 0 0 0 1px rgba(255,255,255,0.22), inset 0 1px 0 rgba(255,255,255,0.35)',
    },
    grain: null,
    // The glass catching the room light: a soft diagonal band.
    sheen: 'linear-gradient(115deg, rgba(255,255,255,0.20) 0%, rgba(255,255,255,0.07) 26%, rgba(255,255,255,0) 42%, rgba(255,255,255,0) 70%, rgba(255,255,255,0.05) 86%, rgba(255,255,255,0) 100%)',
    button: 'radial-gradient(circle at 30% 24%, #ffffff 0%, #d6d9de 14%, #7c8087 40%, #2a2c30 68%, #0c0d0f 100%)',
    buttonEdge: 'inset 0 0 0 1px rgba(255,255,255,0.35), inset 0 -3px 5px rgba(0,0,0,0.6), 0 2px 4px rgba(0,0,0,0.6)',
    label: 'rgba(255,255,255,0.82)',
    socket: 'linear-gradient(160deg, #191a1d, #0b0b0d)',
  },
};

const ON = '#4dff6a';

// The plate's own fan mark: three blades round a hub (MUI has no fan icon —
// its 'Toys' glyph is a toy car).
function FanGlyph({ size, color }) {
  const blade = 'M12 12 C10.2 8.4 10.6 4.6 12.6 3.2 C14.8 4.8 14.6 8.6 12 12 Z';
  return (
    <Box component="svg" viewBox="0 0 24 24" sx={{ width: size, height: size, display: 'block' }}>
      {[0, 120, 240].map((a) => <path key={a} d={blade} fill={color} transform={`rotate(${a} 12 12)`} />)}
      <circle cx="12" cy="12" r="1.7" fill={color} />
    </Box>
  );
}
const AMBER = '#ffb300';

/** The width of an element, live — the plate is laid out from it. */
function useWidth() {
  const ref = useRef(null);
  const [w, setW] = useState(0);
  useLayoutEffect(() => {
    const el = ref.current;
    if (!el) return undefined;
    setW(el.getBoundingClientRect().width);
    if (typeof ResizeObserver === 'undefined') return undefined;
    const ro = new ResizeObserver((entries) => setW(entries[0].contentRect.width));
    ro.observe(el);
    return () => ro.disconnect();
  }, []);
  return [ref, w];
}

/** One round touch button. `state`: 'on' | 'off' | 'unknown'. */
function TouchButton({
  b, look, glyph, state, label, onClick, onContextMenu, title, labelColor,
}) {
  const on = state === 'on';
  const glyphColor = state === 'unknown' ? 'rgba(200,205,212,0.45)' : (on ? ON : 'rgba(255,255,255,0.85)');
  return (
    <Box sx={{ width: b * 1.5, display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
      <Tooltip title={title || ''} disableInteractive>
        <ButtonBase
          onClick={onClick}
          onContextMenu={onContextMenu}
          aria-label={title}
          sx={{
            width: b,
            height: b,
            borderRadius: '50%',
            background: look.button,
            boxShadow: on
              ? `${look.buttonEdge}, 0 0 0 2px ${ON}, 0 0 ${Math.round(b * 0.45)}px ${ON}99`
              : look.buttonEdge,
            transition: 'box-shadow 160ms ease',
            '&:hover': { filter: 'brightness(1.12)' },
          }}
        >
          <Typography
            component="span"
            sx={{
              color: glyphColor,
              fontWeight: 300,
              fontSize: b * 0.5,
              lineHeight: 1,
              letterSpacing: glyph === '||' ? '-0.06em' : 0,
              textShadow: on ? `0 0 ${Math.round(b * 0.25)}px ${ON}` : '0 1px 1px rgba(0,0,0,0.6)',
              userSelect: 'none',
            }}
          >
            {glyph}
          </Typography>
        </ButtonBase>
      </Tooltip>
      {label != null && (
        <Typography
          noWrap
          title={label}
          sx={{
            mt: `${Math.round(b * 0.12)}px`,
            maxWidth: b * 1.5,
            color: labelColor,
            fontSize: Math.max(9, Math.min(12, b * 0.26)),
            fontWeight: 600,
            lineHeight: 1.1,
            textAlign: 'center',
          }}
        >
          {label}
        </Typography>
      )}
    </Box>
  );
}

/** A fan column: + / fan / − and the LED strip of running speeds. */
function FanColumn({
  b, look, label, speeds, value, known, onSet,
}) {
  const running = known && value > FAN_OFF;
  const step = running ? speeds.indexOf(value) : -1;     // -1 = off (or a value off the ladder)
  const lit = running ? (step >= 0 ? step + 1 : speeds.length) : 0;
  const up = () => onSet(!running ? speeds[0] : speeds[Math.min(speeds.length - 1, step + 1)]);
  const down = () => onSet(!running || step <= 0 ? FAN_OFF : speeds[step - 1]);
  const toggle = () => onSet(running ? FAN_OFF : speeds[0]);
  const fanState = !known ? 'unknown' : (running ? 'on' : 'off');
  const dot = Math.max(3, Math.round(b * 0.11));

  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: `${Math.round(b * 0.35)}px` }}>
      <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: `${Math.round(b * 0.18)}px` }}>
        <TouchButton b={b} look={look} glyph="+" state={known ? 'off' : 'unknown'} onClick={up} title="Faster" />
        <Box sx={{ position: 'relative' }}>
          <TouchButton b={b} look={look} glyph="" state={fanState} onClick={toggle} title={running ? 'Fan off' : 'Fan on'} />
          <Box sx={{ position: 'absolute', top: b * 0.22, left: b * 0.47, pointerEvents: 'none' }}>
            <FanGlyph
              size={b * 0.56}
              color={fanState === 'on' ? ON : (fanState === 'unknown' ? 'rgba(200,205,212,0.45)' : 'rgba(255,255,255,0.85)')}
            />
          </Box>
        </Box>
        <TouchButton b={b} look={look} glyph="−" state={known ? 'off' : 'unknown'} onClick={down} title="Slower" />
        {label && (
          <Typography noWrap title={label} sx={{ maxWidth: b * 1.6, color: look.label, fontSize: Math.max(9, Math.min(12, b * 0.26)), fontWeight: 600 }}>
            {label}
          </Typography>
        )}
      </Box>
      {/* The LED strip: one dot per running speed, lit bottom-up to the speed
          the DEVICE reports. */}
      <Box sx={{ display: 'flex', flexDirection: 'column-reverse', gap: `${Math.round(b * 0.17)}px` }}>
        {speeds.map((s, i) => (
          <Box
            key={s}
            sx={{
              width: dot,
              height: dot * 1.6,
              borderRadius: dot,
              bgcolor: i < lit ? AMBER : 'rgba(255,255,255,0.16)',
              boxShadow: i < lit ? `0 0 ${dot * 2}px ${AMBER}` : 'none',
            }}
          />
        ))}
      </Box>
    </Box>
  );
}

/** The 3-pin socket — drawn, never controlled. */
function Socket({ b, look, glossy }) {
  const s = b * 2.6;
  const hole = (d) => ({
    width: d,
    height: d,
    borderRadius: '50%',
    background: glossy
      ? 'radial-gradient(circle at 35% 30%, #d9dce0 0%, #6d7076 45%, #0c0d0f 72%)'
      : 'radial-gradient(circle at 40% 35%, #151618 0%, #0a0a0b 70%)',
    boxShadow: 'inset 0 1px 2px rgba(0,0,0,0.8)',
  });
  return (
    <Tooltip title="Socket (not controlled from the app)" disableInteractive>
      <Box
        sx={{
          width: s, height: s, borderRadius: `${s * 0.24}px`, background: look.socket,
          boxShadow: 'inset 0 2px 6px rgba(0,0,0,0.55), 0 0 0 1px rgba(255,255,255,0.08)',
          position: 'relative', flexShrink: 0,
        }}
      >
        <Box sx={{ position: 'absolute', top: s * 0.2, left: '50%', transform: 'translateX(-50%)', ...hole(s * 0.17) }} />
        <Box sx={{ position: 'absolute', bottom: s * 0.22, left: s * 0.27, ...hole(s * 0.14) }} />
        <Box sx={{ position: 'absolute', bottom: s * 0.22, right: s * 0.27, ...hole(s * 0.14) }} />
      </Box>
    </Tooltip>
  );
}

/** The decorative honeycomb grille between the two rows. */
function Grille({ b }) {
  const d = b * 0.42;
  return (
    <Box
      sx={{
        width: d, height: d, borderRadius: '50%', opacity: 0.45,
        backgroundImage: 'radial-gradient(rgba(255,255,255,0.55) 0.9px, transparent 1.1px)',
        backgroundSize: `${Math.max(3, d / 5)}px ${Math.max(3, d / 5)}px`,
      }}
    />
  );
}

export default function GlassPlateCard({ device, subtitle, large = false }) {
  const { userId } = useApp();
  const navigate = useNavigate();
  const uno = device.UniqueNumber;
  const plate = plateFor(device.DeviceID);

  // ── Finish ────────────────────────────────────────────────────────────────
  const [finish, setFinish] = useState(() => cachedFinish(uno) || FINISH.MATT);
  useEffect(() => {
    let alive = true;
    getGlassFinish(device).then((f) => { if (alive) setFinish(f); });
    const off = onFinishChange((u, f) => { if (alive && String(u) === String(uno)) setFinish(f); });
    return () => { alive = false; off(); };
  }, [device, uno]);
  const look = LOOK[finish] || LOOK[FINISH.MATT];
  const glossy = finish === FINISH.GLOSSY;

  // ── State: panel switches + fans, or the standalone 2M fan ────────────────
  const panelCount = plate.standaloneFan ? 0 : plate.switches + plate.fans;
  const {
    states, known, values, toggleRelay, setAll, setChannel,
  } = useRelayStatus(panelCount ? uno : null, panelCount || 1, userId);
  const [soloStatus, setSoloStatus, liveAt] = useDeviceStatus(uno);
  // The 2M fan seeds from the server's last status, as the phone's dial does —
  // a live frame that lands first is never overwritten.
  useEffect(() => {
    if (!plate.standaloneFan) return undefined;
    let alive = true;
    fetchSeedStatus(uno).then((raw) => { if (alive && raw) setSoloStatus((cur) => cur || raw); });
    return () => { alive = false; };
  }, [uno, plate.standaloneFan, setSoloStatus]);
  const soloSpeed = plate.standaloneFan ? parseFanSpeed(soloStatus) : null;

  // Names, matched on the Output NUMBER.
  const [outputs, setOutputs] = useState([]);
  const reloadOutputs = useCallback(() => {
    getOutputs(uno).then((o) => setOutputs(Array.isArray(o?.data) ? o.data : [])).catch(() => {});
  }, [uno]);
  useEffect(() => { reloadOutputs(); }, [reloadOutputs]);
  const outName = (i) => rowForOutput(outputs, i + 1)?.OutputName || `Switch ${i + 1}`;

  // ── Right-click: Schedule / Button Settings ───────────────────────────────
  const [ctx, setCtx] = useState(null);
  const [settingsFor, setSettingsFor] = useState(null);
  const openMenu = (e, i) => {
    e.preventDefault();
    setCtx({ top: e.clientY - 6, left: e.clientX + 2, output: i + 1, name: outName(i) });
  };
  const goSchedule = (output, name) => navigate(`/schedule/${uno}/${output}`, { state: { deviceId: device.DeviceID, name } });

  // ── Geometry from the measured width ──────────────────────────────────────
  const [plateRef, width] = useWidth();
  const cols = useMemo(() => switchColumns(plate), [plate]);
  const twoRows = cols.some((c) => c.length === 2);
  const units = 2 * 1.4                                   // side margins
    + cols.length * 1.5                                    // switch columns
    + plate.fans * 2.6                                     // fan column + LEDs + gap
    + (plate.socket ? 3.4 : 0)
    + (cols.length && (plate.fans || plate.socket) ? 0.6 : 0);
  const b = width ? Math.max(20, Math.min(large ? 64 : 50, width / units)) : 36;
  // ⚠️ PLATE HEIGHT IS CONSTANT, NOT DERIVED FROM `b`. `b` shrinks as a plate
  // gains columns, a fan or a socket (they add to `units`), and the old
  // `minHeight: b * 4.6` carried that straight into the height — so a 7+socket
  // plate came out short and a 5-gang tall, side by side. Now the height is
  // fixed off the MAX button size for the shell (large vs dashboard) and the
  // buttons, whatever size they are for this plate's column count, sit centred
  // in it. Every 2-row/fan plate is the same height; every 1-row plate is the
  // same shorter height. Same proportion across the whole dashboard.
  const bMax = large ? 64 : 50;
  const plateH = bMax * (twoRows || plate.fans ? 4.6 : 2.6);
  // The master treats EVERY output as a reason to switch the plate OFF: any
  // switch on, OR any fan above its off step (10 — the ladder is 10,30…100). So
  // one output on → `alloff`, and only a fully-off plate → `allon`. Fans count
  // (a 7+2 is 9 outputs). This replaces the old `allOn` (every switch), which
  // sent `allon` while some outputs were already on.
  const anyFanOn = plate.fans > 0 && Array.from(
    { length: plate.fans },
    (_, k) => values[plate.switches + k],
  ).some((v) => v != null && v !== '' && Number(v) > FAN_OFF);
  const anyOn = (plate.switches > 0 && states.slice(0, plate.switches).some(Boolean))
    || anyFanOn;
  const allKnown = plate.switches > 0 && known.slice(0, plate.switches).every(Boolean);

  const stateOf = (i) => (!known[i] ? 'unknown' : (states[i] ? 'on' : 'off'));

  const slot = (s) => (s.kind === 'master' ? (
    <TouchButton
      key="master" b={b} look={look} glyph="||" labelColor={look.label}
      state={!allKnown ? 'unknown' : (anyOn ? 'on' : 'off')}
      label="Master" title={anyOn ? 'Master — all off' : 'Master — all on'}
      onClick={() => setAll(!anyOn)}
    />
  ) : (
    <TouchButton
      key={s.index} b={b} look={look} glyph="|" labelColor={look.label}
      state={stateOf(s.index)} label={outName(s.index)} title={outName(s.index)}
      onClick={() => toggleRelay(s.index)}
      onContextMenu={(e) => openMenu(e, s.index)}
    />
  ));

  return (
    <Card sx={{ p: 1.25, borderRadius: 3, height: '100%', display: 'flex', flexDirection: 'column', gap: 1, bgcolor: 'background.paper', border: 1, borderColor: 'divider' }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
        <Box sx={{ minWidth: 0, flex: 1 }}>
          {subtitle && (
            <Typography color="text.secondary" noWrap sx={{ display: 'block', lineHeight: 1.2, fontSize: CARD_TYPE.subtitle }}>
              {subtitle}
            </Typography>
          )}
          <Typography fontWeight={700} color="text.primary" noWrap sx={{ fontSize: CARD_TYPE.title }}>
            {device.DeviceName || uno}
          </Typography>
        </Box>
        <Typography color="text.secondary" sx={{ fontSize: 10.5, fontWeight: 700, letterSpacing: 0.4 }}>
          {`${plate.modules}M · ${glossy ? 'GLOSSY' : 'MATT'}`}
        </Typography>
        <DeviceStatusDot device={device} live={liveAt} />
        <DeviceCardMenu device={device} onChanged={reloadOutputs} />
      </Box>

      {/* ── The plate ── */}
      <Box
        ref={plateRef}
        sx={{
          position: 'relative',
          borderRadius: `${Math.round(b * 0.4)}px`,
          overflow: 'hidden',
          ...look.plate,
          px: `${b * 1.4}px`,
          py: `${b * 0.9}px`,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          gap: `${b * 0.6}px`,
          height: plateH,
        }}
      >
        {look.grain && (
          <Box sx={{ position: 'absolute', inset: 0, backgroundImage: look.grain, backgroundSize: '3px 3px', pointerEvents: 'none' }} />
        )}
        {look.sheen && (
          <Box sx={{ position: 'absolute', inset: 0, background: look.sheen, pointerEvents: 'none' }} />
        )}

        {/* Switches + master, column by column (wall order). */}
        {cols.length > 0 && (
          <Box sx={{ position: 'relative', display: 'flex', gap: 0, alignItems: 'center' }}>
            {cols.map((col, ci) => (
              // eslint-disable-next-line react/no-array-index-key
              <Box key={ci} sx={{ display: 'flex', flexDirection: 'column', gap: `${b * 0.55}px` }}>
                {col.map(slot)}
              </Box>
            ))}
            {twoRows && (
              <Box sx={{ position: 'absolute', left: '50%', top: '50%', transform: 'translate(-50%, -50%)', pointerEvents: 'none' }}>
                <Grille b={b} />
              </Box>
            )}
          </Box>
        )}

        {/* Panel fans. */}
        {!plate.standaloneFan && Array.from({ length: plate.fans }, (_, f) => {
          const idx = plate.switches + f;
          const raw = values && values[idx];
          const fanKnown = raw !== undefined && raw !== null && raw !== '';
          return (
            <FanColumn
              key={`fan-${f}`} b={b} look={look}
              label={rowForOutput(outputs, idx + 1)?.OutputName || (plate.fans > 1 ? `Fan ${f + 1}` : 'Fan')}
              speeds={PANEL_SPEEDS} value={fanKnown ? Number(raw) : null} known={fanKnown}
              onSet={(v) => setChannel(idx, v)}
            />
          );
        })}

        {/* The standalone 2M fan: its own ladder and command. */}
        {plate.standaloneFan && (
          <FanColumn
            b={b} look={look} label={device.DeviceName || 'Fan'}
            speeds={SOLO_SPEEDS.filter((s) => s > FAN_OFF)}
            value={soloSpeed} known={soloSpeed != null}
            onSet={(v) => publishCommand(uno, fanCommand(v), userId)}
          />
        )}

        {plate.socket && <Socket b={b} look={look} glossy={glossy} />}
      </Box>

      <Menu
        open={ctx !== null}
        onClose={() => setCtx(null)}
        anchorReference="anchorPosition"
        anchorPosition={ctx ? { top: ctx.top, left: ctx.left } : undefined}
      >
        <MenuItem disabled sx={{ opacity: 0.7 }}>
          <ListItemText primaryTypographyProps={{ variant: 'caption', fontWeight: 700 }}>{ctx?.name}</ListItemText>
        </MenuItem>
        <MenuItem onClick={() => { const c = ctx; setCtx(null); if (c) goSchedule(c.output, c.name); }}>
          <ListItemIcon><ScheduleIcon fontSize="small" /></ListItemIcon>
          <ListItemText>Schedule</ListItemText>
        </MenuItem>
        <MenuItem onClick={() => { const c = ctx; setCtx(null); if (c) setSettingsFor(c.output); }}>
          <ListItemIcon><TuneIcon fontSize="small" /></ListItemIcon>
          <ListItemText>Button Settings</ListItemText>
        </MenuItem>
      </Menu>

      <ButtonSettingsDialog
        open={settingsFor != null}
        uniqueNumber={uno}
        output={settingsFor ?? 1}
        onClose={() => setSettingsFor(null)}
        onSaved={reloadOutputs}
        onOpenSchedule={() => { const o = settingsFor; setSettingsFor(null); goSchedule(o, outName(o - 1)); }}
      />
    </Card>
  );
}
